import React, { useState } from 'react';
import { makeStyles } from '@mui/styles';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import { CircularProgress, Typography, TextField, IconButton, Stack, Chip, Autocomplete, Checkbox, useMediaQuery } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { useDispatch, useSelector } from 'react-redux';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import { DateRange } from '@mui/lab/DateRangePicker';
import CalendarIcon from '../../icons/CalendarIcon.svg';
import CalendarModal from '../../pmui/shared/CalendarModal';
import { DocumentTypes, PropertyDTO } from '@inosio/payments-sdk';
import { getFriendlyWordsFromDocumentType } from '../../pmui/owners/utils';
import { getOwnerDocuments, getOwnerProperties, selectOwnerDocumentsState } from '../documents/sagas/documentsSlice';
import { format } from 'date-fns';
import SearchInputV2 from '../../pmui/shared/SearchInput.V2';

const useStyles = makeStyles((theme) => ({
  commonInput: {
    minWidth: '220px',
    '&.MuiInputBase-root': {
      borderRadius: 24,
    },
    '& .MuiInputBase-root': {
      borderRadius: 24,
    },
  },
}));

const defaultStartDate = new Date();
const defaultEndDate = new Date();
defaultStartDate.setFullYear(defaultStartDate.getFullYear() - 1);

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

interface FilterProps {
  rangeSize: number;
  startIndex: number;
  ownerUuid: string;
}

interface IDocumentSearchStates {
  DocumentType?: string;
  Properties?: string[];
  StartDate?: Date | null;
  EndDate?: Date | null;
  DocumentDescription?: string;
}

export default function Filter({ rangeSize, startIndex, ownerUuid }: FilterProps) {
  const matchesMobile = useMediaQuery('(max-width:420px)');
  const matchesIpad = useMediaQuery('(max-width:768px)');
  const classes = useStyles();
  const dispatch = useDispatch();
  const [documentType, setDocumentType] = React.useState<DocumentTypes | 'ALL'>('ALL');
  const [properties, setProperties] = React.useState<PropertyDTO[]>([]);
  const [dateRange, setDateRange] = React.useState<DateRange<Date> | []>([]);
  const [openDateRangeChip, setOpenDateRangeChip] = useState(false);
  const [openCalendarModal, setOpenCalendarModal] = React.useState<boolean>(false);
  const [searchValueText, setSearchValueText] = useState('');
  const documentTypeOptions = [
    DocumentTypes.OwnerStatement,
    DocumentTypes.VendorInvoice,
    DocumentTypes.VendorEstimate,
    DocumentTypes.PropertyManagementAgreement,
    DocumentTypes.Lease,
    DocumentTypes.LeaseAddendum,
    DocumentTypes.OwnerDocumentOther,
    DocumentTypes.UtilityBill,
    DocumentTypes.OtherManagementDocuments,
  ];
  const { Properties, isFetchingProperties } = useSelector(selectOwnerDocumentsState);

  const fetchProperties = () => {
    dispatch({
      type: getOwnerProperties.type,
      payload: {
        ownerUuid: ownerUuid,
      },
    });
  };

  React.useEffect(() => {
    fetchProperties();
  }, []);

  function handleSearch(newState: IDocumentSearchStates = {}) {
    const paramDocumentType = newState.DocumentType ?? documentType;
    const paramPropertyUuids = newState.Properties ?? properties.map((p) => p.Uuid);
    const paramStartDate = newState.StartDate ?? dateRange[0];
    const paramEndDate = newState.EndDate ?? dateRange[1];
    const documentDescription = newState.DocumentDescription ?? searchValueText;

    dispatch({
      type: getOwnerDocuments.type,
      payload: {
        ownerUuid: ownerUuid,
        ownerDocumentSearchParamsDto: {
          RangeSize: rangeSize,
          StartIndex: startIndex,
          DocumentType: paramDocumentType === 'ALL' ? undefined : paramDocumentType,
          PropertyUuids: paramPropertyUuids,
          ...(paramStartDate ? { StartDate: format(paramStartDate, 'yyyy-MM-dd') } : {}),
          ...(paramEndDate ? { EndDate: format(paramEndDate, 'yyyy-MM-dd') } : {}),
          DocumentDescription: documentDescription,
        },
      },
    });
  }

  React.useEffect(() => {
    handleSearch();
  }, [rangeSize, startIndex, ownerUuid]);

  return (
    <Stack>
      <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'flex-start', md: 'center' }} spacing={{ xs: 2, md: 2, lg: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} flexWrap="wrap" alignItems={{ xs: 'flex-start', md: 'flex-start' }} marginRight="auto" flex={1} margin={-1}>
          <Stack direction="row" spacing="8px" alignItems="center" margin={1}>
            <Typography
              sx={{
                fontFamily: 'Work Sans',
                color: '#14142B',
                fontSize: '16px',
                fontWeight: '400',
                lineHeight: '22px',
              }}
            >
              Keyword
            </Typography>
            <SearchInputV2
              handleSearch={handleSearch}
              searchValueChangeHandler={(e) => {
                setSearchValueText(e.target.value);
              }}
              searchValue={searchValueText}
              isSearchValueEmpty={false}
              width={matchesMobile ? '180px' : matchesIpad ? '200px' : undefined}
              showClearSearchValueIcon={true}
              clearSearchValueHandler={() => {
                setSearchValueText('');
                handleSearch({
                  DocumentDescription: '',
                } as IDocumentSearchStates);
              }}
            />
          </Stack>
          <Stack direction="row" spacing="8px" alignItems="center" margin={1}>
            <Typography
              sx={{
                fontFamily: 'Work Sans',
                color: '#14142B',
                fontSize: '16px',
                fontWeight: '400',
                lineHeight: '22px',
              }}
            >
              Type
            </Typography>
            <Select
              labelId="type-select-label"
              id="type-select"
              value={documentType as DocumentTypes}
              onChange={(event: SelectChangeEvent) => {
                setDocumentType(event.target.value as DocumentTypes);
                handleSearch({ DocumentType: event.target.value as DocumentTypes } as IDocumentSearchStates);
              }}
              sx={{ borderRadius: '24px', height: '42px', minWidth: '160px' }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    borderRadius: '24px',
                  },
                },
              }}
            >
              <MenuItem value={'ALL'}>Show All</MenuItem>
              {documentTypeOptions.map((option: DocumentTypes) => (
                <MenuItem value={option} key={option}>
                  {option === DocumentTypes.OwnerDocumentOther ? 'Other' : getFriendlyWordsFromDocumentType(option)}
                </MenuItem>
              ))}
            </Select>
          </Stack>
          <Stack direction="row" spacing="8px" alignItems="" margin={1} mr={2} maxWidth="680px">
            <Typography
              sx={{
                fontFamily: 'Work Sans',
                color: '#14142B',
                fontSize: '16px',
                fontWeight: '400',
                lineHeight: '22px',
                marginTop: '10px',
              }}
            >
              Properties
            </Typography>
            <Autocomplete
              multiple
              id="properties-address"
              className={classes.commonInput}
              options={Properties}
              getOptionLabel={(option: any) => option.Name}
              disableCloseOnSelect
              loading={isFetchingProperties.valueOf()}
              value={properties}
              onInputChange={(event, newInputValue) => {}}
              onChange={(event, value) => {
                setProperties(value);
                handleSearch({
                  Properties: value.map((p) => p.Uuid),
                } as IDocumentSearchStates);
              }}
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Checkbox icon={icon} checkedIcon={checkedIcon} style={{ marginRight: 8 }} checked={selected} />
                  {option.Name}
                </li>
              )}
              renderInput={(params) => {
                return (
                  <TextField
                    {...params}
                    size="small"
                    label=""
                    placeholder=""
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {isFetchingProperties ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                );
              }}
            />
          </Stack>
          <Stack spacing="8px" direction="row" alignItems="center" margin={1} ml={0} pr={1} marginRight={'auto'}>
            <IconButton aria-label="calendar" onClick={() => setOpenCalendarModal(true)}>
              <img src={CalendarIcon} alt="calendar icon" width="24px" height="24px" />
            </IconButton>
            {openDateRangeChip && (
              <Chip
                label={`${(dateRange[0]?.getMonth() || 0) + 1}/${dateRange && dateRange[0]?.getDate()}/${dateRange && dateRange[0]?.getFullYear().toString().substring(2)} -
            ${(dateRange[1]?.getMonth() || 0) + 1}/${dateRange && dateRange[1]?.getDate()}/${dateRange && dateRange[1]?.getFullYear().toString().substring(2)}`}
                sx={{
                  border: '2px #39A2DB solid',
                  bgcolor: '#ffffff',
                  color: '#39A2DB',
                  height: '42px',
                  fontSize: '16px',
                  lineHeight: '22px',
                  borderRadius: '24px',
                }}
                onDelete={() => {
                  setOpenDateRangeChip(false);
                  setDateRange([defaultStartDate, defaultEndDate]);
                  handleSearch({
                    StartDate: defaultStartDate,
                    EndDate: defaultEndDate,
                  } as IDocumentSearchStates);
                }}
                deleteIcon={<CloseIcon sx={{ color: '#39A2DB !important' }} fontSize="small" />}
              />
            )}
          </Stack>
        </Stack>
      </Stack>
      <CalendarModal
        value={dateRange}
        handleClose={() => {
          setOpenCalendarModal(false);
        }}
        open={openCalendarModal}
        handleCalendarSubmit={(val: DateRange<Date>) => {
          setDateRange(val);
          setOpenCalendarModal(false);
          setOpenDateRangeChip(true);
          handleSearch({
            StartDate: val[0],
            EndDate: val[1],
          } as IDocumentSearchStates);
        }}
      />
    </Stack>
  );
}
